import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import ReceiptViewer from "@/components/ReceiptViewer";
import AnimatedNumber from "@/components/AnimatedNumber";
import { RibSection } from "@/components/RibSection";
import { AntennaCard } from "@/components/AntennaCard";
import { AdvancePicker } from "@/components/AdvancePicker";
import { CATEGORY_ICONS, CATEGORY_COLORS, money, fmtDate, initials, avatarColor } from "@/lib/utils2";
import { waReport, waAlert } from "@/lib/whatsapp";

function CatIcon({ category, className }) {
  const Ico = Icons[CATEGORY_ICONS[category]] || Icons.Package;
  return <Ico className={className} />;
}

const TABS = ["feed", "bank_account", "cash_box", "reports"];

export default function Finances() {
  const { t, lang } = useApp();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const q = searchParams.get("tab");
    return TABS.includes(q) ? q : "feed";
  });
  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && TABS.includes(q)) setTab(q);
  }, [searchParams]);

  return (
    <div className="space-y-4" data-testid="finances-page">
      <div>
        <h1 className="text-2xl font-bold">{t("transparency_feed")}</h1>
        <p className="text-muted-foreground">{t("transparency_sub")}</p>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`fin-tab-${tb}`}
            className={`h-10 px-4 rounded-full font-semibold text-sm whitespace-nowrap transition-colors ${
              tab === tb ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"
            }`}>
            {t(tb)}
          </button>
        ))}
      </div>

      {tab === "feed" && <Feed />}
      {tab === "bank_account" && <Bank />}
      {tab === "cash_box" && <Cash />}
      {tab === "reports" && <Reports />}
    </div>
  );
}

function Feed() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const isTres = isRole("tresorier");
  const isPres = isRole("president");
  const [items, setItems] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [openQ, setOpenQ] = useState(null);
  const [qText, setQText] = useState("");

  const load = useCallback(() =>
    api.get("/feed").then((r) => setItems(r.data)).catch(() => toast.error(t("load_failed"))), [t]);
  useEffect(() => { load(); }, [load]);

  const askQuestion = async (id) => {
    if (!qText.trim()) return;
    await api.post(`/expenses/${id}/questions`, { text: qText });
    setQText(""); setOpenQ(null);
    toast.success(t("send"));
    load();
  };

  const answer = async (expId, qid, text) => {
    await api.post(`/expenses/${expId}/questions/${qid}/answer`, { text });
    toast.success("OK");
    load();
  };

  return (
    <div className="space-y-3" data-testid="transparency-feed">
      {items.length === 0 && <Empty text={t("empty_generic")} />}
      {items.map((item) => (
        <div key={item.id} className={`rounded-2xl bg-card border p-4 ${item.status === "annule" ? "opacity-60 border-dashed" : "border-border"}`}
          data-testid={`feed-item-${item.id}`}>
          <div className="flex items-start gap-3">
            {item.type === "revenue" ? (
              <div className="h-12 w-12 rounded-xl bg-success/15 flex items-center justify-center shrink-0">
                <Icons.Radio className="h-6 w-6 text-success" />
              </div>
            ) : (
              <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: (CATEGORY_COLORS[item.category] || "#94A3B8") + "22" }}>
                <CatIcon category={item.category} className="h-6 w-6" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold truncate">{item.type === "revenue" ? item.label : item.supplier}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.type === "expense" && <span className="font-semibold">{t("cat_" + item.category)} · </span>}
                    {fmtDate(item.date, lang)}
                  </p>
                </div>
                <p className={`font-black text-lg whitespace-nowrap ${item.type === "revenue" ? "text-success" : "text-foreground"}`}>
                  {item.type === "revenue" ? "+" : "-"}{money(item.amount)} <span className="text-xs">{t("currency")}</span>
                </p>
              </div>
              {item.status === "annule" && (
                <span className="inline-block mt-1 text-xs font-semibold text-destructive bg-destructive/10 px-2 py-0.5 rounded-full">{t("cancelled")}</span>
              )}
              {item.type === "expense" && item.status !== "annule" && (
                <ExpensePayment item={item} isTres={isTres} isPres={isPres} onDone={load} t={t} />
              )}
              <div className="flex items-center gap-2 mt-2">
                {item.proof_url ? (
                  <button onClick={() => setReceipt(item.proof_url)} data-testid={`receipt-btn-${item.id}`}
                    className="text-xs font-semibold text-majorelle flex items-center gap-1">
                    <Icons.Receipt className="h-3.5 w-3.5" /> {t("view_receipt")}
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icons.ImageOff className="h-3.5 w-3.5" /> {t("no_receipt")}
                  </span>
                )}
                {item.type === "expense" && (
                  <button onClick={() => setOpenQ(openQ === item.id ? null : item.id)} data-testid={`question-btn-${item.id}`}
                    className="text-xs font-semibold text-muted-foreground flex items-center gap-1 ms-auto">
                    <Icons.MessageCircleQuestion className="h-3.5 w-3.5" /> {t("ask_question")}
                    {item.questions?.length > 0 && <span className="bg-terracotta text-white rounded-full px-1.5">{item.questions.length}</span>}
                  </button>
                )}
              </div>

              {/* Questions thread */}
              {item.questions?.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-border pt-3">
                  {item.questions.map((q) => (
                    <div key={q.id} className="text-sm">
                      <p className="font-semibold flex items-center gap-1"><Icons.HelpCircle className="h-3.5 w-3.5 text-majorelle" /> {q.text}</p>
                      <p className="text-xs text-muted-foreground ms-5">— {q.author}</p>
                      {q.answer ? (
                        <p className="ms-5 mt-1 bg-accent rounded-lg px-3 py-2 text-sm">{q.answer.text} <span className="text-xs text-muted-foreground">— {q.answer.author}</span></p>
                      ) : isRole("president", "tresorier") ? (
                        <AnswerBox onSend={(txt) => answer(item.id, q.id, txt)} placeholder={t("answer")} />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {openQ === item.id && (
                <div className="mt-3 flex gap-2">
                  <input value={qText} onChange={(e) => setQText(e.target.value)} placeholder={t("your_question")}
                    data-testid={`question-input-${item.id}`}
                    className="flex-1 h-11 rounded-xl border border-input bg-background px-3 text-sm outline-none" />
                  <button onClick={() => askQuestion(item.id)} data-testid={`question-send-${item.id}`}
                    className="h-11 px-4 rounded-xl bg-brand text-white font-semibold text-sm">{t("send")}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {receipt && <ReceiptViewer path={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}

function AnswerBox({ onSend, placeholder }) {
  const [txt, setTxt] = useState("");
  return (
    <div className="ms-5 mt-1 flex gap-2">
      <input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={placeholder}
        className="flex-1 h-10 rounded-lg border border-input bg-background px-3 text-sm outline-none" />
      <button onClick={() => { if (txt.trim()) { onSend(txt); setTxt(""); } }}
        className="h-10 px-3 rounded-lg bg-majorelle text-white text-sm font-semibold">OK</button>
    </div>
  );
}

function ExpensePayment({ item, isTres, isPres, onDone, t }) {
  const [choosing, setChoosing] = useState(null);   // 'pay' | 'reimburse' | 'advance'
  const [busy, setBusy] = useState(false);
  const [advId, setAdvId] = useState("");
  const [advConfirmed, setAdvConfirmed] = useState(false);
  const ps = item.payment_status, pm = item.payment_method, rs = item.reimbursement_status;

  const act = async (endpoint, method, extra = {}) => {
    setBusy(true);
    try {
      await api.put(`/expenses/${item.id}/${endpoint}`, { method, ...extra });
      toast.success(t("saved"));
      setChoosing(null);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const saveAdvance = () => {
    if (!advId) return toast.error(t("advance_select_required"));
    if (!advConfirmed) return toast.error(t("advance_confirm_required"));
    act("pay", "avance", { advanced_by_id: advId });
  };

  let badge;
  if (ps === "en_attente") badge = { txt: t("badge_pending"), cls: "bg-warning/15 text-warning", icon: "Clock" };
  else if (pm === "avance") badge = rs === "rembourse"
    ? { txt: t("badge_reimbursed"), cls: "bg-success/15 text-success", icon: "CheckCheck" }
    : { txt: t("badge_advance"), cls: "bg-terracotta/15 text-terracotta", icon: "HandCoins" };
  else if (pm === "cash") badge = { txt: t("badge_cash"), cls: "bg-brand/10 text-brand dark:text-primary", icon: "Wallet" };
  else badge = { txt: t("badge_bank"), cls: "bg-majorelle/10 text-majorelle", icon: "Landmark" };
  const Ico = Icons[badge.icon] || Icons.Circle;

  const showPay = (isTres || isPres) && ps === "en_attente";
  const showReimburse = isTres && pm === "avance" && rs === "du";

  return (
    <>
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`payment-row-${item.id}`}>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${badge.cls}`} data-testid={`payment-badge-${item.id}`}>
        <Ico className="h-3 w-3" /> {badge.txt}
      </span>
      {item.sans_facture && (
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 bg-amber-500/15 text-amber-600" data-testid={`sans-facture-badge-${item.id}`}>
          <Icons.Handshake className="h-3 w-3" /> {t("sans_facture_badge")}
        </span>
      )}
      {pm === "avance" && item.advanced_by && (
        <span className="text-[11px] text-muted-foreground">· {t("advanced_by_label")}: {item.advanced_by}</span>
      )}
      {showPay && !choosing && (
        <button onClick={() => setChoosing("pay")} data-testid={`pay-btn-${item.id}`}
          className="text-[11px] font-bold text-white bg-brand rounded-full px-2.5 py-1">{t("pay_now")}</button>
      )}
      {showReimburse && !choosing && (
        <button onClick={() => setChoosing("reimburse")} data-testid={`reimburse-btn-${item.id}`}
          className="text-[11px] font-bold text-white bg-terracotta rounded-full px-2.5 py-1">{t("reimburse_now")}</button>
      )}
      {choosing && choosing !== "advance" && (
        <span className="flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">{choosing === "pay" ? t("choose_pay_method") : t("reimburse_title")}:</span>
          {(choosing === "reimburse" || isTres) && (
            <button disabled={busy} onClick={() => act(choosing, "cash")} data-testid={`method-cash-${item.id}`}
              className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-brand text-white disabled:opacity-60">{t("pm_cash")}</button>
          )}
          <button disabled={busy} onClick={() => act(choosing, "bank")} data-testid={`method-bank-${item.id}`}
            className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-majorelle text-white disabled:opacity-60">{t("pm_bank")}</button>
          {choosing === "pay" && (
            <button disabled={busy} onClick={() => { setAdvId(""); setAdvConfirmed(false); setChoosing("advance"); }} data-testid={`method-avance-${item.id}`}
              className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-terracotta text-white disabled:opacity-60">{t("pay_advanced")}</button>
          )}
          <button onClick={() => setChoosing(null)} className="text-muted-foreground"><Icons.X className="h-3.5 w-3.5" /></button>
        </span>
      )}
    </div>
    {choosing === "advance" && (
      <div className="mt-2 rounded-2xl border border-border bg-secondary/40 p-3 space-y-2" data-testid={`advance-form-${item.id}`}>
        <AdvancePicker t={t} value={advId} confirmed={advConfirmed}
          onPick={(id) => { setAdvId(id); setAdvConfirmed(false); }}
          onConfirm={setAdvConfirmed} testidPrefix={`feed-${item.id}`} />
        <div className="flex gap-2">
          <button disabled={busy} onClick={saveAdvance} data-testid={`advance-save-${item.id}`}
            className="flex-1 h-10 rounded-xl bg-terracotta text-white font-bold text-sm disabled:opacity-60">{t("save")}</button>
          <button onClick={() => setChoosing(null)} data-testid={`advance-cancel-${item.id}`}
            className="h-10 px-4 rounded-xl border border-input text-sm font-semibold">{t("cancel")}</button>
        </div>
      </div>
    )}
    {item.sans_facture && item.artisan_name && (
      <p className="text-[11px] text-muted-foreground mt-1" data-testid={`artisan-info-${item.id}`}>
        {item.artisan_name} · {item.artisan_phone} — {item.service_desc}
      </p>
    )}
    </>
  );
}

function Cash() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const isTres = isRole("tresorier");
  const isPres = isRole("president");
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(() =>
    api.get("/cash").then((r) => setData(r.data)).catch(() => toast.error(t("load_failed"))), [t]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-10 text-center text-muted-foreground">{t("loading")}</div>;

  const onProof = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof(r.data.url); toast.success(t("proof_ok"));
    } catch { toast.error("Erreur"); } finally { setUploading(false); }
  };

  const requestTopup = async () => {
    if (!amount || parseFloat(amount) <= 0) return toast.error(t("topup_amount"));
    if (!proof) return toast.error(t("topup_proof_required"));
    setBusy(true);
    try {
      await api.post("/cash/topups", { amount: parseFloat(amount), label: label || undefined, withdrawal_proof_url: proof });
      toast.success(t("saved")); setAmount(""); setLabel(""); setProof(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const decide = async (id, action, reason) => {
    try {
      await api.put(`/cash/topups/${id}/${action}`, action === "reject" ? { reason: reason || "" } : {});
      toast.success(t("saved")); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const MV = {
    topup: { icon: "ArrowDownToLine", color: "text-success" },
    expense: { icon: "Receipt", color: "text-foreground" },
    reimbursement: { icon: "HandCoins", color: "text-terracotta" },
    reversal: { icon: "Undo2", color: "text-muted-foreground" },
  };
  const pending = data.topups.filter((tp) => tp.status === "en_attente");

  return (
    <div className="space-y-4" data-testid="cash-panel">
      <div className="rounded-3xl bg-gradient-to-br from-brand to-brand-dark text-white p-6 relative overflow-hidden" data-testid="cash-balance-card">
        <div className="absolute inset-0 zellige-overlay" />
        <div className="relative z-10">
          <p className="text-white/70 text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <Icons.Wallet className="h-4 w-4" /> {t("cash_box_full")}
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <AnimatedNumber value={data.balance} className="text-4xl font-black" data-testid="cash-balance-value" />
            <span className="text-lg font-bold text-white/80">{t("currency")}</span>
          </div>
        </div>
      </div>

      {/* Récap des avances à rembourser — visible président + trésorier */}
      {(isTres || isPres) && <AdvancesRecap isTres={isTres} onCashChanged={load} t={t} lang={lang} />}

      {/* Demandes d'alimentation en attente (validation président) */}
      {pending.length > 0 && (
        <div className="space-y-2" data-testid="pending-topups">
          {pending.map((tp) => (
            <div key={tp.id} className="rounded-2xl bg-warning/10 border border-warning/30 p-4" data-testid={`topup-${tp.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold">{money(tp.amount)} {t("currency")}</p>
                  <p className="text-xs text-muted-foreground">{tp.label} · {t("requested_by")}: {tp.requested_by}</p>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-warning mt-1">
                    <Icons.Clock className="h-3 w-3" /> {t("awaiting_president")}
                  </span>
                </div>
                {tp.withdrawal_proof_url && (
                  <a href={fileUrl(tp.withdrawal_proof_url)} target="_blank" rel="noopener noreferrer" className="text-majorelle" data-testid={`topup-proof-${tp.id}`}>
                    <Icons.Paperclip className="h-4 w-4" />
                  </a>
                )}
              </div>
              {isPres && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => decide(tp.id, "approve")} data-testid={`topup-approve-${tp.id}`}
                    className="flex-1 h-10 rounded-xl bg-brand text-white font-semibold text-sm flex items-center justify-center gap-1.5">
                    <Icons.Check className="h-4 w-4" /> {t("approve")}
                  </button>
                  <button onClick={() => decide(tp.id, "reject", window.prompt(t("reject_reason")) || "")} data-testid={`topup-reject-${tp.id}`}
                    className="flex-1 h-10 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm flex items-center justify-center gap-1.5">
                    <Icons.X className="h-4 w-4" /> {t("reject")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Formulaire d'alimentation — trésorier uniquement */}
      {isTres && (
        <div className="rounded-2xl bg-card border border-border p-4 space-y-3" data-testid="topup-form">
          <input ref={fileRef} type="file" accept="application/pdf,image/*" capture="environment" onChange={onProof} className="hidden" />
          <div>
            <p className="font-bold flex items-center gap-2"><Icons.ArrowDownToLine className="h-5 w-5 text-brand dark:text-primary" /> {t("topup_title")}</p>
            <p className="text-xs text-muted-foreground">{t("topup_sub")}</p>
          </div>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("topup_amount")}
            data-testid="topup-amount" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("topup_label")}
            data-testid="topup-label" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="topup-proof-btn"
            className={`w-full h-12 rounded-xl border font-semibold text-sm flex items-center justify-center gap-2 ${proof ? "border-brand text-brand dark:text-primary" : "border-input"}`}>
            <Icons.Paperclip className="h-4 w-4" /> {proof ? t("proof_ok") : t("topup_proof")}
          </button>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Icons.Info className="h-3 w-3" /> {t("double_val_note")}</p>
          <button onClick={requestTopup} disabled={busy} data-testid="topup-submit"
            className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{t("request_topup")}</button>
        </div>
      )}

      {/* Mouvements de caisse */}
      <div>
        <h3 className="font-bold mb-2">{t("cash_movements")}</h3>
        {data.movements.length === 0 && <Empty text={t("no_movements")} />}
        <div className="space-y-2">
          {data.movements.map((m) => {
            const meta = MV[m.reason] || MV.expense;
            const Ico = Icons[meta.icon] || Icons.Circle;
            const isIn = m.direction === "in";
            return (
              <div key={m.id} className="rounded-xl bg-card border border-border p-3 flex items-center gap-3" data-testid={`cash-mv-${m.id}`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${isIn ? "bg-success/15" : "bg-secondary"}`}>
                  <Ico className={`h-5 w-5 ${isIn ? "text-success" : "text-muted-foreground"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{t("mov_" + m.reason)}{m.label ? ` · ${m.label}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(m.date, lang)} · {m.created_by}</p>
                </div>
                <p className={`font-bold whitespace-nowrap ${isIn ? "text-success" : "text-foreground"}`}>
                  {isIn ? "+" : "-"}{money(m.amount)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AdvancesRecap({ isTres, onCashChanged, t, lang }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);          // personne dépliée
  const [confirming, setConfirming] = useState(null); // personne en cours de remboursement
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() =>
    api.get("/expenses/advances/summary").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const doReimburse = async () => {
    setBusy(true);
    try {
      await api.put("/expenses/advances/reimburse-person", { person_id: confirming.person_id, method });
      toast.success(t("saved"));
      setConfirming(null);
      load();
      if (method === "cash") onCashChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (data.people.length === 0) {
    return (
      <div className="rounded-2xl bg-success/10 border border-success/25 p-4 flex items-center gap-3" data-testid="advances-recap-empty">
        <Icons.CheckCircle2 className="h-5 w-5 text-success shrink-0" />
        <p className="text-sm font-semibold text-success">{t("advances_none")}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-card border border-terracotta/40 p-4 space-y-3" data-testid="advances-recap">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold flex items-center gap-2">
            <Icons.HandCoins className="h-5 w-5 text-terracotta" /> {t("advances_recap_title")}
          </p>
          <p className="text-xs text-muted-foreground">{t("advances_recap_sub")}</p>
        </div>
        <div className="text-end">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase">{t("advances_grand_total")}</p>
          <p className="font-black text-terracotta whitespace-nowrap" data-testid="advances-grand-total">
            {money(data.grand_total)} <span className="text-xs">{t("currency")}</span>
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {data.people.map((p) => {
          const key = p.person_id || p.name;
          const expanded = open === key;
          return (
            <div key={key} className="rounded-xl bg-secondary/50 border border-border" data-testid={`advance-person-${key}`}>
              <div className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                  style={{ backgroundColor: avatarColor(p.name) }}>
                  {initials(p.name)}
                </div>
                <button onClick={() => setOpen(expanded ? null : key)} className="min-w-0 flex-1 text-start"
                  data-testid={`advance-person-toggle-${key}`}>
                  <p className="font-bold text-sm truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    {p.count} {t("advance_invoices")}
                    <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </p>
                </button>
                <p className="font-black whitespace-nowrap text-terracotta" data-testid={`advance-person-total-${key}`}>
                  {money(p.total)} <span className="text-[10px]">{t("currency")}</span>
                </p>
                {isTres && (
                  <button onClick={() => { setMethod("cash"); setConfirming(p); }} data-testid={`reimburse-person-btn-${key}`}
                    className="h-9 px-3 rounded-xl bg-terracotta text-white font-bold text-xs shrink-0">
                    {t("reimburse_now")}
                  </button>
                )}
              </div>
              {expanded && (
                <div className="px-3 pb-3 space-y-1.5 border-t border-border pt-2" data-testid={`advance-person-detail-${key}`}>
                  {p.expenses.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-muted-foreground">
                        {e.supplier || t("cat_" + e.category)} · {fmtDate(e.date, lang)}
                      </span>
                      <span className="font-semibold whitespace-nowrap">{money(e.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Popup de confirmation (trésorier) */}
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reimburse-confirm-modal">
          <div className="w-full max-w-sm rounded-3xl bg-card border border-border p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Icons.HandCoins className="h-5 w-5 text-terracotta" />
              <p className="font-bold">{t("reimburse_person_confirm")}</p>
            </div>
            <p className="text-sm">
              {t("reimburse_person_msg")}{" "}
              <span className="font-black text-terracotta">{money(confirming.total)} {t("currency")}</span>{" "}
              {t("to_person")} <span className="font-bold">{confirming.name}</span>{" "}
              ({confirming.count} {t("advance_invoices")}).
            </p>
            <div className="flex gap-2">
              <button onClick={() => setMethod("cash")} data-testid="reimburse-method-cash"
                className={`flex-1 h-11 rounded-xl font-semibold text-sm ${method === "cash" ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
                {t("pm_cash")}
              </button>
              <button onClick={() => setMethod("bank")} data-testid="reimburse-method-bank"
                className={`flex-1 h-11 rounded-xl font-semibold text-sm ${method === "bank" ? "bg-majorelle text-white" : "bg-secondary text-secondary-foreground"}`}>
                {t("pm_bank")}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Icons.Info className="h-3 w-3 shrink-0" /> {t("reimburse_method_hint")}
            </p>
            <div className="flex gap-2">
              <button onClick={doReimburse} disabled={busy} data-testid="reimburse-confirm-btn"
                className="flex-1 h-12 rounded-xl bg-terracotta text-white font-bold disabled:opacity-60 flex items-center justify-center gap-2">
                <Icons.Check className="h-4 w-4" /> {t("confirm_btn")}
              </button>
              <button onClick={() => setConfirming(null)} data-testid="reimburse-cancel-btn"
                className="h-12 px-4 rounded-xl border border-input font-semibold">{t("cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bank() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const canManage = isRole("president", "tresorier");
  const [stmt, setStmt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(null);   // anomaly id being commented
  const [comment, setComment] = useState("");
  const [showMatches, setShowMatches] = useState(false);
  const [antRefresh, setAntRefresh] = useState(0);
  const [viewFile, setViewFile] = useState(null);

  const load = useCallback(() =>
    api.get("/bank/latest").then((r) => setStmt(r.data && r.data.id ? r.data : null)).catch(() => toast.error(t("load_failed"))), [t]);
  useEffect(() => { load(); }, [load]);

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/bank/import", fd);
      toast.success(t("statement_imported"));
      load();
    } catch { toast.error("Erreur d'import"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const submitResolve = async (aid) => {
    if (!comment.trim()) { toast.error(t("recon_comment_required")); return; }
    try {
      await api.put(`/bank/statements/${stmt.id}/anomalies/${aid}/resolve`, { comment });
      toast.success(t("recon_resolved")); setResolving(null); setComment(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const reopen = async (aid) => {
    try { await api.put(`/bank/statements/${stmt.id}/anomalies/${aid}/reopen`); load(); }
    catch { toast.error("Erreur"); }
  };

  const toRevenue = async (aid) => {
    try {
      await api.post(`/bank/statements/${stmt.id}/anomalies/${aid}/to-revenue`);
      toast.success(t("recon_to_revenue_done")); load(); setAntRefresh((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const acceptAll = async () => {
    if (!window.confirm(t("recon_accept_all_confirm"))) return;
    try {
      await api.put(`/bank/statements/${stmt.id}/anomalies/accept-all`);
      toast.success(t("recon_accept_all_done")); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const recon = stmt?.reconciliation;
  const anomalies = recon?.anomalies || [];
  const openAnom = anomalies.filter((a) => a.status !== "resolved");
  const resolvedAnom = anomalies.filter((a) => a.status === "resolved");
  const matches = recon?.matches || [];
  const allGood = anomalies.length === 0 || openAnom.length === 0;

  return (
    <div className="space-y-4" data-testid="bank-panel">
      <AntennaCard refreshKey={antRefresh} />
      {canManage && <RibSection />}
      {viewFile && <ReceiptViewer path={viewFile} onClose={() => setViewFile(null)} />}
      {!stmt && <Empty text={t("no_bank")} />}
      {stmt && (
        <>
          <div className="rounded-3xl bg-card border border-border p-6">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Icons.Landmark className="h-4 w-4" /> {t("official_balance")}
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <AnimatedNumber value={stmt.closing_balance} className="text-4xl font-black" />
              <span className="text-lg font-bold text-muted-foreground">{t("currency")}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t("statement_date")}: {fmtDate(stmt.statement_date, lang)}</p>
            {stmt.file_url && (
              <button onClick={() => setViewFile(stmt.file_url)} data-testid="view-statement-btn"
                className="mt-3 h-9 px-3 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold flex items-center gap-1.5">
                <Icons.Eye className="h-4 w-4" /> {t("consult")}
              </button>
            )}
          </div>

          {/* Statut global du rapprochement */}
          <div className={`rounded-2xl p-4 flex items-start gap-3 ${allGood ? "bg-success/10" : "bg-warning/10"}`} data-testid="reconciliation-status">
            {allGood ? <Icons.CheckCircle2 className="h-6 w-6 text-success shrink-0" /> : <Icons.AlertTriangle className="h-6 w-6 text-warning shrink-0" />}
            <div className="flex-1">
              <p className="font-bold">{allGood ? t("reconciliation_ok") : t("reconciliation_warn")}</p>
              <p className="text-sm text-muted-foreground">
                {t("matched_ops")}: {recon?.matched}/{recon?.total_transactions}
                {openAnom.length > 0 && ` · ${t("recon_open_count")}: ${openAnom.length}`}
              </p>
              <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full bg-majorelle/10 text-majorelle" data-testid="recon-engine">
                {recon?.method === "ai" ? <Icons.Sparkles className="h-3 w-3" /> : <Icons.Cpu className="h-3 w-3" />}
                {recon?.method === "ai" ? t("recon_engine_ai") : t("recon_engine_algo")}
              </span>
            </div>
          </div>

          {/* Anomalies ouvertes (rouge) */}
          {openAnom.length > 0 && (
            <div data-testid="anomalies-section">
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className="font-bold flex items-center gap-2 text-destructive">
                  <Icons.AlertTriangle className="h-4 w-4" /> {t("recon_anomalies")} ({openAnom.length})
                </h3>
                {canManage && (
                  <button onClick={acceptAll} data-testid="anomalies-accept-all"
                    className="h-8 px-3 rounded-lg bg-secondary text-secondary-foreground text-xs font-semibold flex items-center gap-1.5 shrink-0">
                    <Icons.CheckCheck className="h-4 w-4" /> {t("recon_accept_all")}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {openAnom.map((a) => (
                  <div key={a.id} className="rounded-2xl bg-destructive/5 border border-destructive/30 p-4" data-testid={`anomaly-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-1 ${a.kind === "bank_only" ? "bg-majorelle/15 text-majorelle" : "bg-terracotta/15 text-terracotta"}`}>
                          {a.kind === "bank_only" ? t("recon_bank_only") : t("recon_app_only")}
                        </span>
                        <p className="font-bold truncate">{a.label || "—"}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(a.date, lang)}</p>
                        {a.ai_note && <p className="text-xs text-muted-foreground italic mt-0.5">« {a.ai_note} »</p>}
                      </div>
                      <p className={`font-black whitespace-nowrap ${a.direction === "credit" ? "text-success" : "text-foreground"}`}>
                        {a.direction === "credit" ? "+" : "-"}{money(a.amount)}
                      </p>
                    </div>
                    {canManage && (resolving === a.id ? (
                      <div className="mt-3 space-y-2">
                        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                          placeholder={t("recon_comment_ph")} data-testid={`anomaly-comment-${a.id}`}
                          className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none" />
                        <div className="flex gap-2">
                          <button onClick={() => submitResolve(a.id)} data-testid={`anomaly-resolve-submit-${a.id}`}
                            className="flex-1 h-10 rounded-xl bg-brand text-white font-semibold text-sm flex items-center justify-center gap-1.5">
                            <Icons.Check className="h-4 w-4" /> {t("recon_mark_resolved")}
                          </button>
                          <button onClick={() => { setResolving(null); setComment(""); }}
                            className="h-10 px-3 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold">{t("cancel")}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {a.kind === "bank_only" && a.direction === "credit" && (
                          <button onClick={() => toRevenue(a.id)} data-testid={`anomaly-to-revenue-${a.id}`}
                            className="h-9 px-3 rounded-xl bg-success/15 text-success font-semibold text-sm flex items-center gap-1.5">
                            <Icons.PiggyBank className="h-4 w-4" /> {t("recon_to_revenue")}
                          </button>
                        )}
                        <button onClick={() => { setResolving(a.id); setComment(""); }} data-testid={`anomaly-resolve-${a.id}`}
                          className="h-9 px-3 rounded-xl bg-destructive/10 text-destructive font-semibold text-sm flex items-center gap-1.5">
                          <Icons.MessageSquarePlus className="h-4 w-4" /> {t("recon_resolve")}
                        </button>
                        <button onClick={() => window.open(waAlert({
                          title: `${t("recon_anomalies")} — ${a.label || ""}`,
                          body: `${a.kind === "bank_only" ? t("recon_bank_only") : t("recon_app_only")} · ${a.direction === "credit" ? "+" : "-"}${money(a.amount)} ${t("currency")} · ${fmtDate(a.date, lang)}`,
                          lang,
                        }), "_blank")} data-testid={`anomaly-share-${a.id}`}
                          className="h-9 px-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm flex items-center gap-1.5">
                          <Icons.Send className="h-4 w-4" /> {t("wa_alert")}
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Anomalies résolues (vert, avec commentaire) */}
          {resolvedAnom.length > 0 && (
            <div data-testid="resolved-section">
              <h3 className="font-bold mb-2 flex items-center gap-2 text-success">
                <Icons.CheckCheck className="h-4 w-4" /> {t("recon_resolved_plural")} ({resolvedAnom.length})
              </h3>
              <div className="space-y-2">
                {resolvedAnom.map((a) => (
                  <div key={a.id} className="rounded-2xl bg-success/5 border border-success/25 p-4" data-testid={`anomaly-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-bold truncate">{a.label || "—"}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(a.date, lang)}</p>
                      </div>
                      <p className="font-bold whitespace-nowrap text-muted-foreground">{a.direction === "credit" ? "+" : "-"}{money(a.amount)}</p>
                    </div>
                    <p className="mt-2 text-sm bg-accent rounded-lg px-3 py-2">{a.comment}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-[11px] text-muted-foreground">{t("recon_resolved_by")}: {a.resolved_by} · {fmtDate(a.resolved_at, lang)}</p>
                      {canManage && (
                        <button onClick={() => reopen(a.id)} data-testid={`anomaly-reopen-${a.id}`}
                          className="text-[11px] font-semibold text-majorelle flex items-center gap-1"><Icons.Undo2 className="h-3 w-3" /> {t("recon_reopen")}</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Correspondances (vert) — repliables */}
          {matches.length > 0 && (
            <div data-testid="matches-section">
              <button onClick={() => setShowMatches((s) => !s)} data-testid="toggle-matches"
                className="w-full flex items-center justify-between font-bold text-success">
                <span className="flex items-center gap-2"><Icons.CheckCircle2 className="h-4 w-4" /> {t("recon_matches")} ({matches.length})</span>
                <Icons.ChevronDown className={`h-4 w-4 transition-transform ${showMatches ? "rotate-180" : ""}`} />
              </button>
              {showMatches && (
                <div className="space-y-2 mt-2">
                  {matches.map((m, i) => (
                    <div key={`${m.app_ref_id}-${i}`} className="rounded-xl bg-success/5 border border-success/20 p-3" data-testid={`match-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                            <Icons.Link2 className="h-3.5 w-3.5 text-success shrink-0" /> {m.bank_label}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">↔ {m.app_label} · {fmtDate(m.app_date, lang)}</p>
                          {m.note && <p className="text-[11px] text-muted-foreground italic">{m.note}</p>}
                        </div>
                        <p className={`font-bold whitespace-nowrap ${m.direction === "credit" ? "text-success" : "text-foreground"}`}>
                          {m.direction === "credit" ? "+" : "-"}{money(m.amount)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {canManage && (
        <label className="block">
          <input type="file" accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={importFile} data-testid="bank-import-input" />
          <span className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform">
            <Icons.Upload className="h-5 w-5" /> {busy ? t("reconciling") : t("import_statement")}
          </span>
        </label>
      )}
    </div>
  );
}

function ChartBox({ className, children }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setSize({ w: Math.round(cr.width), h: Math.round(cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}>
      {size.w > 0 && size.h > 0 && (
        <ResponsiveContainer width={size.w} height={size.h}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  );
}

function Reports() {
  const { t, lang } = useApp();
  const [dash, setDash] = useState(null);
  const [balances, setBalances] = useState({ bank: 0, cash: 0 });
  const now = new Date();
  const year = now.getFullYear();
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/dashboard").then((r) => setDash(r.data));
    // Soldes banque + caisse pour enrichir le message WhatsApp du rapport.
    Promise.all([
      api.get("/bank/latest").then((r) => r.data?.closing_balance || 0).catch(() => 0),
      api.get("/cash").then((r) => r.data?.balance || 0).catch(() => 0),
    ]).then(([bank, cash]) => setBalances({ bank, cash }));
  }, []);
  if (!dash) return <div className="py-10 text-center text-muted-foreground">{t("loading")}</div>;

  const pieData = dash.categories.map((c) => ({ name: t("cat_" + c.category), value: c.amount, color: CATEGORY_COLORS[c.category] }));
  const barData = dash.quarters.map((q) => ({ name: `T${q.quarter}`, revenue: q.revenue, expense: q.expense }));
  const qData = dash.quarters.find((q) => q.quarter === quarter) || { revenue: 0, expense: 0, saved: 0 };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/reports/quarterly?quarter=${quarter}&year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rapport-T${quarter}-${year}-rahma-bat-a.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const shareWhatsApp = () => {
    // Message trilingue (langue courante) avec recettes, dépenses, épargne et soldes banque/caisse.
    const url = waReport({
      period: `T${quarter} ${year}`,
      revenue: qData.revenue, expense: qData.expense, saved: qData.saved,
      bank: balances.bank, cash: balances.cash, lang,
    });
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-5" data-testid="reports-panel">
      <div className="rounded-2xl bg-card border border-border p-5 space-y-3" data-testid="report-generator">
        <h3 className="font-bold">{t("quarterly_report")}</h3>
        <p className="text-sm text-muted-foreground">{t("report_period")} · {year}</p>
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((q) => (
            <button key={q} onClick={() => setQuarter(q)} data-testid={`report-quarter-${q}`}
              className={`flex-1 h-11 rounded-xl font-semibold text-sm transition-colors ${quarter === q ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
              T{q}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <MiniStat label={t("total_revenue")} value={`${money(qData.revenue)}`} color="text-success" />
          <MiniStat label={t("total_expense")} value={`${money(qData.expense)}`} color="text-foreground" />
          <MiniStat label={t("saved_this_period")} value={`${money(qData.saved)}`} color={qData.saved >= 0 ? "text-success" : "text-destructive"} />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button onClick={downloadPdf} disabled={busy} data-testid="download-report-btn"
            className="h-12 rounded-xl bg-brand text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            <Icons.FileDown className="h-5 w-5" /> {busy ? t("loading") : t("download_report")}
          </button>
          <button onClick={shareWhatsApp} data-testid="share-whatsapp-btn"
            className="h-12 rounded-xl bg-[#25D366] text-white font-bold flex items-center justify-center gap-2">
            <Icons.Share2 className="h-5 w-5" /> {t("share_whatsapp")}
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-5">
        <h3 className="font-bold mb-3">{t("expenses_by_category")}</h3>
        <div className="h-56">
          <ChartBox className="h-full">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2}>
                {pieData.map((e) => <Cell key={e.name} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => `${money(v)} ${t("currency")}`} />
            </PieChart>
          </ChartBox>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {pieData.map((e) => (
            <div key={e.name} className="flex items-center gap-2 text-sm">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: e.color }} />
              <span className="flex-1 truncate">{e.name}</span>
              <span className="font-semibold">{money(e.value)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-5">
        <h3 className="font-bold mb-3">{t("quarterly_summary")}</h3>
        <div className="h-52">
          <ChartBox className="h-full">
            <BarChart data={barData}>
              <XAxis dataKey="name" tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => `${money(v)} ${t("currency")}`} />
              <Bar dataKey="revenue" fill="#10B981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expense" fill="#C05A46" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartBox>
        </div>
        <div className="space-y-1 mt-2">
          {dash.quarters.map((q) => (
            <div key={q.quarter} className="flex justify-between text-sm">
              <span className="font-semibold">{t("quarter")} {q.quarter}</span>
              <span className="text-success font-semibold">{t("saved_this_period")}: {money(q.saved)} {t("currency")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-2">
      <p className="text-[10px] text-muted-foreground font-semibold leading-tight">{label}</p>
      <p className={`font-black text-sm mt-0.5 ${color}`}>{value}</p>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="py-16 flex flex-col items-center text-center gap-3">
      <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center">
        <Icons.Inbox className="h-8 w-8 text-muted-foreground" />
      </div>
      <p className="text-muted-foreground font-medium">{text}</p>
    </div>
  );
}
